import {
  Board,
  ManufacturingStepStatus,
  Shipment,
  updateManufacturingStep,
} from "../../../reducers/uploadedBoard";
import Checkbox from "../../common/Checkbox";
import useSendBoardEvent, {
  BoardTransitionEvent,
  MachineGroup,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/sendBoardEvent";
import {
  ChecklistTable,
  StatusTableCell,
  StatusTableContainer,
  StatusTableHeader,
  StatusTableRow,
} from "../../internal/BoardInventory/BoardInventoryStyles";
import React, { useContext } from "react";
import LoadingOverlay from "../../LoadingOverlay";
import Icon from "../../common/Icon";
import { useCustomConfirm } from "../../../utils/hooks/alerts";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../../../utils/hooks/commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";
import { useBoardFromContext } from "../../../utils/hooks/commonly-stored/uploaded-board/board";
import LoadingIcon from "../../common/LoadingIcon";
import { useAppDispatch } from "../../../utils/hooks/store";
import useShipmentsFromContext from "../../../utils/hooks/commonly-stored/uploaded-board/shipments";

export const manufacturingStepLabels: Record<string, string> = {
  boards_purchased: "Boards purchased",
  stencils_purchased: "Stencil(s) purchased",
  parts_purchased: "Parts purchased",
  placement_checks: "Placement checks",
  programmed: "Programmed",
  smt_bottom: "SMT bottom",
  smt_top: "SMT top",
  hand_soldering: "Hand soldering",
  inspection: "Inspection",
  cleaning: "Cleaning",
  coating: "Coating",
  packaging: "Packaging",
  smt_bottom_ready: "SMT bottom",
  smt_bottom_complete: "SMT bottom",
  smt_top_ready: "SMT top",
  smt_top_complete: "SMT top",
  hand_soldering_ready: "Hand soldering",
  hand_soldering_complete: "Hand soldering",
  inspection_ready: "Inspection",
  inspection_complete: "Inspection",
  cleaning_ready: "Cleaning",
  cleaning_complete: "Cleaning",
  coating_ready: "Coating",
  coating_complete: "Coating",
  packaging_ready: "Packaging",
  packaging_complete: "Packaging",
  all_materials_purchased: "All materials purchased",
};

export default function ManufacturingStatus() {
  const dispatch = useAppDispatch();

  const board = useBoardFromContext().board as Board;
  const { shipmentId } = useContext(BoardIdContext) as BoardShipmentIds;
  const shipments = useShipmentsFromContext(board.id) as Shipment[];
  const shipment = shipments.find((shipment) => shipment.id == shipmentId);
  const { sendBoardEvent, loading } = useSendBoardEvent(undefined);
  const customConfirm = useCustomConfirm();

  if (shipment === undefined || shipmentId === null) {
    return <LoadingIcon />;
  }

  const onMarkStepAsDone = (step: string) => {
    const update = {
      shipment_id: shipmentId,
      step_status_update: [
        {
          step_name: step,
          step_status: ManufacturingStepStatus.COMPLETE,
        },
      ],
    };
    customConfirm(`Are you sure you want to mark ${step} as complete`, () => {
      if (step == "stencils_purchased" || step == "boards_purchased") {
        shipments.forEach((shipment) => {
          void sendBoardEvent(board.id, {
            machine_group: MachineGroup.SHIPMENT,
            model_id: shipment.id,
            event: BoardTransitionEvent.MANUFACTURING_STEP_UPDATE,
            update: update,
          });
        });
      } else {
        void sendBoardEvent(board.id, {
          machine_group: MachineGroup.SHIPMENT,
          model_id: shipmentId,
          event: BoardTransitionEvent.MANUFACTURING_STEP_UPDATE,
          update: update,
        });
      }
      dispatch(updateManufacturingStep({ boardId: board.id, update }));
    });
  };

  const onMarkStepAsNotApplicable = (step: string) => {
    customConfirm(
      `Are you sure you want to mark ${step} as not applicable? This will remove it from the checklist for this project.`,
      () => {
        const update = {
          shipment_id: shipmentId,
          step_status_update: [
            {
              step_name: step,
              step_status: ManufacturingStepStatus.NOT_APPLICABLE,
            },
          ],
        };
        void sendBoardEvent(board.id, {
          machine_group: MachineGroup.BOARD,
          model_id: board.id,
          event: BoardTransitionEvent.MANUFACTURING_STEP_UPDATE,
          update: update,
        });
        dispatch(updateManufacturingStep({ boardId: board.id, update }));
      },
    );
  };
  const renderReadyCheckbox = (status: ManufacturingStepStatus) => {
    if (status === ManufacturingStepStatus.READY) {
      return <Icon src={"/icons/check_green.svg"} alt={"check"} />;
    } else if (status === ManufacturingStepStatus.WAITING) {
      return <Icon src={"/icons/fatal.svg"} alt={"check"} />;
    } else {
      return null;
    }
  };

  const renderCompleteCheckbox = (
    status: ManufacturingStepStatus,
    step: string,
  ) => {
    if (status === ManufacturingStepStatus.COMPLETE) {
      return <Icon src={"/icons/check_green.svg"} alt={"check"} />;
    } else {
      return (
        <Checkbox checked={false} onChange={() => onMarkStepAsDone(step)} />
      );
    }
  };

  const renderNACheckbox = (status: ManufacturingStepStatus, step: string) => {
    if (status === ManufacturingStepStatus.NOT_APPLICABLE) {
      return <Icon src={"/icons/check_green.svg"} alt={"check"} />;
    } else {
      return (
        <Checkbox
          checked={false}
          onChange={() => onMarkStepAsNotApplicable(step)}
        />
      );
    }
  };
  return (
    <StatusTableContainer>
      <LoadingOverlay loading={loading} text={"Loading"} />
      <ChecklistTable>
        <thead>
          <tr>
            <StatusTableHeader>Step</StatusTableHeader>
            <StatusTableHeader>Ready</StatusTableHeader>
            <StatusTableHeader>Done</StatusTableHeader>
            <StatusTableHeader>Mark N/A</StatusTableHeader>
          </tr>
        </thead>
        <tbody>
          {Object.entries(shipment.manufacturing_checklist.steps).map(
            ([step, status], i) => {
              return (
                <StatusTableRow key={`${step}-${i}`}>
                  <StatusTableCell>
                    {manufacturingStepLabels[step] !== undefined
                      ? manufacturingStepLabels[step]
                      : step}
                  </StatusTableCell>
                  <StatusTableCell>
                    {renderReadyCheckbox(status)}
                  </StatusTableCell>
                  <StatusTableCell>
                    {renderCompleteCheckbox(status, step)}
                  </StatusTableCell>
                  <StatusTableCell>
                    {renderNACheckbox(status, step)}
                  </StatusTableCell>
                </StatusTableRow>
              );
            },
          )}
        </tbody>
      </ChecklistTable>
    </StatusTableContainer>
  );
}
